import { createSlice } from '@reduxjs/toolkit';
import { ActiveCapture, CardUrlState, SelectedFeatureState, SelectedMethaneFeature } from '../../../../types';

/** 
 * This slice contains the state variables for the map feature that has been selected when
 * querying from URL parameters, as well as reducers for setting/resetting these variables.
 *
 * Currently, the app only supports querying for methane features (point sources, area emissions,
 * and targets) from the URL. In the future, if we were to support querying for OGI features from
 * URL parameters, then it would have to be updated with the appropriate state variables and reducers
 * to do so. Link to ticket:
   DP-4301: https://methanesat.atlassian.net/browse/DP-4301?atlOrigin=eyJpIjoiMGRlOTVlNzgzOTgxNDE3MjhlNDYyNTI4Y2EwZDYyMDkiLCJwIjoiaiJ9
*/

const initialState: SelectedFeatureState = {
    selectedMethaneFeature: {
        itemId: '',
        coordinates: [],
        targetDate: '',
        targetId: ''
    },
    selectedOGIFeature: {
        object: null
    },
    selectedPlume: null,
    activeCapture: null,
    urlParamsInitialized: false,
    fetchingDrawerInfo: false,
    emissionsDrawerCardData: {} as CardUrlState
};

export const selectedFeatureSlice = createSlice({
    name: 'selectedFeature',
    initialState,
    reducers: {
        setSelectedFeatureParams: (state, { payload }: { payload: SelectedMethaneFeature }) => {
            state.selectedMethaneFeature = {
                ...state.selectedMethaneFeature,
                ...payload
            };
        },
        setSelectedOGIFeature: (state, { payload }) => {
            state.selectedOGIFeature.object = payload;
        },
        resetSelectedFeatureParams: (state) => {
            state.selectedMethaneFeature = initialState.selectedMethaneFeature;
            state.emissionsDrawerCardData = {} as CardUrlState;
            state.selectedOGIFeature.object = null;
        },
        setSelectedPlume: (state, { payload }) => {
            state.selectedPlume = payload;
        },
        setActiveCapture: (state, { payload }: { payload: ActiveCapture }) => {
            state.activeCapture = payload;
        },
        clearActiveCapture: (state) => {
            state.activeCapture = null;
        },
        setUrlParamsInitialized: (state) => {
            state.urlParamsInitialized = true;
        },
        setFetchingDrawerInfo: (state, { payload }) => {
            state.fetchingDrawerInfo = payload;
        },
        setEmissionsDrawerCardData: (state, { payload }: { payload: CardUrlState }) => {
            state.emissionsDrawerCardData = payload;
        }
    }
});

// reducers
export const {
    setSelectedFeatureParams,
    setSelectedOGIFeature,
    resetSelectedFeatureParams,
    setSelectedPlume,
    setUrlParamsInitialized,
    setFetchingDrawerInfo,
    setActiveCapture,
    clearActiveCapture,
    setEmissionsDrawerCardData
} = selectedFeatureSlice.actions;

export default selectedFeatureSlice.reducer;
